const FBX_BINARY_MAGIC = 'Kaydara FBX Binary  \0\x1a\0';
const FBX_VERSION_OFFSET = FBX_BINARY_MAGIC.length;
const FBX_64BIT_VERSION = 7500;
const FBX_32BIT_NODE_HEADER_SIZE = 13;
const FBX_64BIT_NODE_HEADER_SIZE = 25;

const textDecoder = new TextDecoder();

const AXIS_KEYS = [
  'UpAxis',
  'UpAxisSign',
  'OriginalUpAxis',
  'OriginalUpAxisSign',
] as const;

type AxisPropertyKey = (typeof AXIS_KEYS)[number];

export type FbxAxisName = 'x' | 'y' | 'z';

export interface FbxUpAxisMetadata {
  upAxis: FbxAxisName;
  upSign: 1 | -1;
  property: 'UpAxis' | 'OriginalUpAxis';
  format: 'ascii' | 'binary';
}

type ParsedAxisPropertyMap = Partial<Record<AxisPropertyKey, number>>;

type BinaryFbxNode = {
  name: string;
  props: unknown[];
  childrenOffset: number;
  nextOffset: number;
};

function isBinaryFbx(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < FBX_BINARY_MAGIC.length) {
    return false;
  }

  const bytes = new Uint8Array(buffer, 0, FBX_BINARY_MAGIC.length);
  return textDecoder.decode(bytes) === FBX_BINARY_MAGIC;
}

function parseAxisName(value: number | undefined): FbxAxisName | null {
  switch (value) {
    case 0:
      return 'x';
    case 1:
      return 'y';
    case 2:
      return 'z';
    default:
      return null;
  }
}

function parseAxisSign(value: number | undefined): 1 | -1 {
  return value === -1 ? -1 : 1;
}

function buildUpAxisMetadata(
  props: ParsedAxisPropertyMap | null,
  format: FbxUpAxisMetadata['format'],
): FbxUpAxisMetadata | null {
  if (!props) return null;

  const upAxis = parseAxisName(props.UpAxis);
  if (upAxis) {
    return {
      upAxis,
      upSign: parseAxisSign(props.UpAxisSign),
      property: 'UpAxis',
      format,
    };
  }

  const originalUpAxis = parseAxisName(props.OriginalUpAxis);
  if (originalUpAxis) {
    return {
      upAxis: originalUpAxis,
      upSign: parseAxisSign(props.OriginalUpAxisSign),
      property: 'OriginalUpAxis',
      format,
    };
  }

  return null;
}

function getNodeNullRecordSize(version: number): number {
  return version >= FBX_64BIT_VERSION ? FBX_64BIT_NODE_HEADER_SIZE : FBX_32BIT_NODE_HEADER_SIZE;
}

function readUint64AsNumber(view: DataView, offset: number): number {
  const value = Number(view.getBigUint64(offset, true));
  if (!Number.isSafeInteger(value)) {
    throw new Error('FBX node offset exceeds JS safe integer range');
  }
  return value;
}

function readNodeHeader(
  view: DataView,
  offset: number,
  version: number,
): {
  endOffset: number;
  numProperties: number;
  propertyListLen: number;
  nameLen: number;
  headerSize: number;
} | null {
  const use64Bit = version >= FBX_64BIT_VERSION;
  const headerSize = use64Bit
    ? FBX_64BIT_NODE_HEADER_SIZE
    : FBX_32BIT_NODE_HEADER_SIZE;

  if (offset + headerSize > view.byteLength) {
    return null;
  }

  const endOffset = use64Bit
    ? readUint64AsNumber(view, offset)
    : view.getUint32(offset, true);
  const numProperties = use64Bit
    ? readUint64AsNumber(view, offset + 8)
    : view.getUint32(offset + 4, true);
  const propertyListLen = use64Bit
    ? readUint64AsNumber(view, offset + 16)
    : view.getUint32(offset + 8, true);
  const nameLen = view.getUint8(offset + headerSize - 1);

  if (
    endOffset === 0 &&
    numProperties === 0 &&
    propertyListLen === 0 &&
    nameLen === 0
  ) {
    return null;
  }

  return { endOffset, numProperties, propertyListLen, nameLen, headerSize };
}

function skipBinaryFbxProperty(
  view: DataView,
  offset: number,
): { value: unknown; nextOffset: number } {
  if (offset >= view.byteLength) {
    throw new Error('Unexpected end of FBX property data');
  }

  const typeCode = String.fromCharCode(view.getUint8(offset));
  let nextOffset = offset + 1;

  switch (typeCode) {
    case 'Y':
      return { value: view.getInt16(nextOffset, true), nextOffset: nextOffset + 2 };
    case 'C':
      return { value: view.getUint8(nextOffset) !== 0, nextOffset: nextOffset + 1 };
    case 'I':
      return { value: view.getInt32(nextOffset, true), nextOffset: nextOffset + 4 };
    case 'F':
      return { value: view.getFloat32(nextOffset, true), nextOffset: nextOffset + 4 };
    case 'D':
      return { value: view.getFloat64(nextOffset, true), nextOffset: nextOffset + 8 };
    case 'L': {
      const value = Number(view.getBigInt64(nextOffset, true));
      return { value, nextOffset: nextOffset + 8 };
    }
    case 'R':
    case 'S': {
      const length = view.getUint32(nextOffset, true);
      nextOffset += 4;
      const endOffset = nextOffset + length;
      if (endOffset > view.byteLength) {
        throw new Error('FBX string/raw property exceeds buffer length');
      }
      const raw = new Uint8Array(view.buffer, view.byteOffset + nextOffset, length);
      const value = typeCode === 'S' ? textDecoder.decode(raw) : raw;
      return { value, nextOffset: endOffset };
    }
    case 'b':
    case 'c':
    case 'd':
    case 'f':
    case 'i':
    case 'l': {
      const _arrayLength = view.getUint32(nextOffset, true);
      const _encoding = view.getUint32(nextOffset + 4, true);
      const compressedLength = view.getUint32(nextOffset + 8, true);
      nextOffset += 12;
      return { value: null, nextOffset: nextOffset + compressedLength };
    }
    default:
      throw new Error(`Unsupported FBX property type: ${typeCode}`);
  }
}

function readBinaryFbxNode(
  view: DataView,
  offset: number,
  version: number,
): BinaryFbxNode | null {
  const header = readNodeHeader(view, offset, version);
  if (!header) {
    return null;
  }

  const nameOffset = offset + header.headerSize;
  const nameEnd = nameOffset + header.nameLen;
  if (nameEnd > view.byteLength) {
    throw new Error('FBX node name exceeds buffer length');
  }

  const name = textDecoder.decode(
    new Uint8Array(view.buffer, view.byteOffset + nameOffset, header.nameLen),
  );

  let propertyOffset = nameEnd;
  const props: unknown[] = [];
  for (let index = 0; index < header.numProperties; index += 1) {
    const { value, nextOffset } = skipBinaryFbxProperty(view, propertyOffset);
    props.push(value);
    propertyOffset = nextOffset;
  }

  return {
    name,
    props,
    childrenOffset: propertyOffset,
    nextOffset: header.endOffset,
  };
}

function extractBinaryAxisProps(buffer: ArrayBuffer): ParsedAxisPropertyMap | null {
  const view = new DataView(buffer);
  const version = view.getUint32(FBX_VERSION_OFFSET, true);
  const nullRecordSize = getNodeNullRecordSize(version);

  let offset = FBX_VERSION_OFFSET + 4;
  while (offset < view.byteLength) {
    const topNode = readBinaryFbxNode(view, offset, version);
    if (!topNode) break;

    if (topNode.name === 'GlobalSettings') {
      let globalOffset = topNode.childrenOffset;
      while (globalOffset < topNode.nextOffset - nullRecordSize) {
        const globalChild = readBinaryFbxNode(view, globalOffset, version);
        if (!globalChild) break;

        if (globalChild.name === 'Properties70') {
          const props: ParsedAxisPropertyMap = {};
          let propOffset = globalChild.childrenOffset;
          while (propOffset < globalChild.nextOffset - nullRecordSize) {
            const propNode = readBinaryFbxNode(view, propOffset, version);
            if (!propNode) break;

            if (
              propNode.name === 'P' &&
              typeof propNode.props[0] === 'string' &&
              AXIS_KEYS.includes(propNode.props[0] as AxisPropertyKey) &&
              typeof propNode.props[4] === 'number'
            ) {
              props[propNode.props[0] as AxisPropertyKey] = propNode.props[4];
            }

            propOffset = propNode.nextOffset;
          }
          return props;
        }

        globalOffset = globalChild.nextOffset;
      }
    }

    offset = topNode.nextOffset;
  }

  return null;
}

function extractAsciiAxisProps(buffer: ArrayBuffer): ParsedAxisPropertyMap | null {
  const text = textDecoder.decode(new Uint8Array(buffer));

  const readIntProp = (key: AxisPropertyKey): number | undefined => {
    const match = new RegExp(
      `P\\s*:\\s*"${key}"\\s*,\\s*"[^"]*"\\s*,\\s*"[^"]*"\\s*,\\s*"[^"]*"\\s*,\\s*(-?\\d+)`,
      'i',
    ).exec(text);
    if (!match) return undefined;

    const value = Number.parseInt(match[1], 10);
    return Number.isFinite(value) ? value : undefined;
  };

  const props: ParsedAxisPropertyMap = {};
  for (const key of AXIS_KEYS) {
    const value = readIntProp(key);
    if (value != null) {
      props[key] = value;
    }
  }

  return Object.keys(props).length > 0 ? props : null;
}

export function extractFbxUpAxisMetadata(
  buffer: ArrayBuffer,
): FbxUpAxisMetadata | null {
  try {
    if (isBinaryFbx(buffer)) {
      return buildUpAxisMetadata(extractBinaryAxisProps(buffer), 'binary');
    }

    return buildUpAxisMetadata(extractAsciiAxisProps(buffer), 'ascii');
  } catch (error) {
    console.warn('[Cartographer] Failed to parse FBX up-axis metadata:', error);
    return null;
  }
}
